import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { useUser } from "../context/UserContext";
import { getGameSession, submitAnswer } from "../lib/api";
import QuestionCard from "../components/QuestionCard";
import AnswerButtons from "../components/AnswerButtons";
import PageWrapper from "./PageWrapper";

const shuffled = (items) => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

export default function CardsView() {
  const { currentUserId } = useUser();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!currentUserId) return;
    getGameSession(currentUserId).then((gameSession) => {
      setSession(gameSession);
      setQuestions(shuffled(gameSession.questions || []));
      setIndex(0);
    });
  }, [currentUserId]);

  const current = questions && questions[index];

  const handleAnswer = async (value) => {
    if (submitting || !current || !session) return;
    setSubmitting(true);
    try {
      await submitAnswer({
        userId: currentUserId,
        sessionId: session.id,
        questionId: current.id,
        answer: value,
      });
      toast.success("Réponse enregistrée");
      setTimeout(() => {
        setFlipped(false);
        setTimeout(() => {
          setIndex((i) => i + 1);
          setSubmitting(false);
        }, 350);
      }, 300);
    } catch (e) {
      toast.error("Une erreur est survenue");
      setSubmitting(false);
    }
  };

  if (questions === null || session === null) {
    return (
      <PageWrapper>
        <div className="center-note">Chargement…</div>
      </PageWrapper>
    );
  }

  const finished = index >= questions.length;
  const completedBeforeLoad = session.answeredCount || 0;
  const displayedProgress = Math.min(completedBeforeLoad + index + 1, session.questionCount || 20);

  return (
    <PageWrapper>
      <div className="cards-view" data-testid="cards-view">
        {finished ? (
          <motion.div
            className="empty-state"
            data-testid="cards-empty"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2 className="empty-title">Session terminée</h2>
            <p className="empty-text">
              Tu as répondu aux 20 cartes de cette session. La prochaine session sera disponible
              lorsque vous aurez tous les deux terminé celle-ci.
            </p>
            <button className="btn-primary" data-testid="go-matches-btn" onClick={() => navigate("/matches")}>
              Voir nos matchs
            </button>
          </motion.div>
        ) : (
          <>
            <div className="cards-progress">
              Carte {displayedProgress} / {session.questionCount || 20}
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={current.id}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.3 }}
                className="card-stage"
              >
                <QuestionCard question={current} flipped={flipped} onFlip={() => setFlipped(true)} />
              </motion.div>
            </AnimatePresence>

            <div className="answer-slot">
              {flipped && (
                <AnswerButtons question={current} onAnswer={handleAnswer} disabled={submitting} />
              )}
            </div>
          </>
        )}
      </div>
    </PageWrapper>
  );
}
